import { useEffect, useState } from 'react';
import { Link2, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/lib/errors';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { ProjectSearchList } from './ProjectPicker';

function useLink(doc, onLink, close) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pick = async (project) => {
    setBusy(true);
    setError('');
    try {
      await onLink(doc, project);
      close();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not link the document'));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, pick };
}

const linkHint = (versionCount) => `${versionCount > 1 ? `All ${versionCount} versions move with it.` : 'Pick the project it belongs to.'} You can undo this.`;

/**
 * "Link to project" under an Unlinked pill (16 idea 5): pick the project; every version of the document
 * moves with it (PUT /api/documents/{id}). onLink(doc, project) → Promise (throws → shown here).
 * onOpen() lets the page load the project list the first time.
 *
 * asSheet (phones, opened from the card menu "Link to project…"): the same list in a bottom sheet,
 * controlled by `open` / `onOpenChange`; no "Link to project" link is rendered.
 */
export default function LinkProjectPopover({
  doc, versionCount = 1, projectList, onOpen, onLink, className, asSheet = false, open: sheetOpen = false, onOpenChange,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const close = () => (asSheet ? onOpenChange?.(false) : setPopoverOpen(false));
  const { busy, error, setError, pick } = useLink(doc, onLink, close);

  useEffect(() => {
    if (asSheet && sheetOpen) onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asSheet, sheetOpen]);

  const list = (autoFocus) => (
    <>
      {error ? <p role="alert" className="border-b border-line-2 px-3 py-2 text-[12.5px] font-medium text-status-red-ink">{error}</p> : null}
      <ProjectSearchList
        projects={projectList?.projects}
        loading={projectList?.loading}
        error={projectList?.error}
        onRetry={projectList?.retry}
        onPick={busy ? () => {} : pick}
        autoFocus={autoFocus}
        testId={`link-document-list-${doc.id}`}
      />
    </>
  );

  if (asSheet) {
    return (
      <Sheet open={sheetOpen} onOpenChange={(next) => { setError(''); onOpenChange?.(next); }}>
        <SheetContent
          side="bottom"
          overlayClassName="bg-[color:var(--scrim)]"
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-2xl border-line bg-surface p-0 pb-[env(safe-area-inset-bottom)] text-ink"
          data-testid={`link-document-sheet-${doc.id}`}
        >
          <div className="flex items-start gap-2.5 border-b border-line-2 px-4 pb-3 pr-12 pt-4">
            <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
            <div className="min-w-0">
              <SheetTitle className="truncate text-[14px] font-bold text-ink">Link “{doc.title}” to a project</SheetTitle>
              <SheetDescription className="mt-0.5 text-[12.5px] text-ink-3">{linkHint(versionCount)}</SheetDescription>
            </div>
            {busy ? <Loader2 className="ml-auto h-4 w-4 shrink-0 animate-spin text-ink-3" aria-hidden="true" /> : null}
          </div>
          {/* no autofocus: on a phone the keyboard would cover the list */}
          {list(false)}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={(next) => {
        setPopoverOpen(next);
        setError('');
        if (next) onOpen?.();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(event) => event.stopPropagation()}
          className={className || 'mt-1 block rounded-[3px] text-[12px] font-semibold text-brand-ink underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'}
          data-testid={`link-document-${doc.id}`}
        >
          Link to project
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[320px] rounded-xl border-line bg-surface p-0 shadow-overlay"
        onClick={(event) => event.stopPropagation()}
        data-row-click="ignore"
      >
        <div className="flex items-start gap-2 border-b border-line-2 px-3 py-2.5">
          <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
          <div className="min-w-0 text-[12.5px]">
            <p className="truncate font-semibold text-ink">Link “{doc.title}”</p>
            <p className="text-ink-3">{linkHint(versionCount)}</p>
          </div>
          {busy ? <Loader2 className="ml-auto h-4 w-4 shrink-0 animate-spin text-ink-3" aria-hidden="true" /> : null}
        </div>
        {list(true)}
      </PopoverContent>
    </Popover>
  );
}
